import React from "react";
import { StatusNotificationScreen } from "../../../common/StatusNotificationScreen";
import { createApi } from "../../../utils/createApi";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { AxiosError, AxiosResponse } from "axios";
import {
  OnboardingProgressDiv,
  isCameraCheckSupported,
} from "../../Onboarding/OnboardingProgressDiv";
import { handleCreateAutoAddObject } from "./handleCreateAutoAddObject";
import { CameraAutoAddScreen } from "../CameraAutoAddScreen";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { ProgressFinishedScreen } from "../../AddSchedule/ProgressFinishedScreen";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { handleCheckCameras } from "../../CameraCheck/handleCheckCameras";
import { GenetecAlarmOnboardingPrompt } from "../../../common/GenetecAlarmSetup/GenetecAlarmOnboardingPrompt";
import { ImportSettingsOnboardingPrompt } from "../../../common/ImportSettings/ImportSettingsOnboardingPrompt";

// How long "Camera Add successful!" shows before the flow moves on.
const SUCCESS_SCREEN_MS = 2000;

export const handleSubmitAutoAddCameras = async (
  camerasObject: any,
  setVisibleScreen: any,
  setLargerScreen: Dispatcher<boolean>,
  largerScreen: boolean,
  setCameraCheckFinsihedAlertOpen: any,
  setGoodCameras: any,
  setConnectivityCameras: any,
  setLowResolutionCameras: any,
  setCameraCheckResultDownloadURL: any,
  sentinelSite: boolean,
  patriotSite: boolean,
  streamChangesMade: boolean,
  streamPatchObject: any,
  navigate: any
) => {
  const retryFunction = () => {
    handleSubmitAutoAddCameras(
      camerasObject,
      setVisibleScreen,
      setLargerScreen,
      largerScreen,
      setCameraCheckFinsihedAlertOpen,
      setGoodCameras,
      setConnectivityCameras,
      setLowResolutionCameras,
      setCameraCheckResultDownloadURL,
      sentinelSite,
      patriotSite,
      streamChangesMade,
      streamPatchObject,
      navigate
    );
  };
  setLargerScreen(false);
  setVisibleScreen(
    <StatusNotificationScreen
      styling="absolute first-line:font-black font-mulish text-[20px] right-[150px] text-center text-[#283E6D] top-80"
      text={
        <p>
          Camera Add in process.
          <br />
          This might take a few seconds.
        </p>
      }
    />
  );
  const AxiosUI = createApi(``);
  const ActuateUI = createApi(``);
  let nvrCameraFetchFail = false;
  let siteCamerasFetchFail = false;
  // Every camera on the site once auto-add has run. On first onboarding these
  // are exactly the new cameras; when adding to an existing site the import
  // card scopes to them too, and `overwrite: false` leaves anything that
  // already has a value alone.
  let siteCameraIds: number[] = [];

  const sentinelCamsWithErrors: string[] = [];
  const patriotCamsWithErrors: string[] = [];

  const sentinelAlertObject =
    Object.keys(camerasObject).length > 0
      ? camerasObject[Object.keys(camerasObject)[0]].sentinel
      : {
          sentinel_server: "",
          sentinel_site_id: "",
          sentinel_device_address: "",
          draw_ignore_zones: false,
        };

  const patriotAlertObject =
    Object.keys(camerasObject).length > 0
      ? camerasObject[Object.keys(camerasObject)[0]].patriot
      : {
          patriot_server: "",
          patriot_client_no: "",
          patriot_username: "",
          patriot_password: "",
          patriot_zone_number: "",
          patriot_type_number: "",
          patriot_send_video: false,
          draw_ignore_zones: false,
        };

  const createNewStreamsObject = (streams: any) => {
    const updatedStreams = streams;
    updatedStreams.forEach((object: any, index: any) => {
      const updatedSentinelAlerts = object.sentinel_alerts;
      updatedSentinelAlerts.push(sentinelAlertObject);
      updatedStreams[index].sentinel_alerts = updatedSentinelAlerts;
    });
    return updatedStreams;
  };

  const createNewStreamsObjectPatriot = (streams: any) => {
    const updatedStreams = streams;
    updatedStreams.forEach((object: any, index: any) => {
      const updatedPatriotAlerts = object.patriot_alerts || [];
      updatedPatriotAlerts.push(patriotAlertObject);
      updatedStreams[index].patriot_alerts = updatedPatriotAlerts;
    });
    return updatedStreams;
  };

  const streamPatchErrorsCameras: string[] = [];

  const postSentinelAlert = (
    cameraId: number,
    streamsObject: any,
    cameraName: any,
    lastCamera: boolean
  ) => {
    AxiosUI.patch(`/camera/${cameraId}/general_info/`, {
      streams: createNewStreamsObject(streamsObject),
    }).then(
      () => {
        if (lastCamera) {
          if (sentinelCamsWithErrors.length > 0) {
            alert(
              `The following cameras had errors in adding the Sentinel alert: ${sentinelCamsWithErrors.join(
                ", "
              )}`
            );
          }
        }
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          sentinelCamsWithErrors.push(cameraName);
          if (lastCamera) {
            if (sentinelCamsWithErrors.length > 0) {
              alert(
                `The following cameras had errors in adding the Sentinel alert: ${sentinelCamsWithErrors.join(
                  ", "
                )}`
              );
            }
          }
        });
      }
    );
  };

  const postPatriotAlert = (
    cameraId: number,
    streamsObject: any,
    cameraName: any,
    lastCamera: boolean
  ) => {
    AxiosUI.patch(`/camera/${cameraId}/general_info/`, {
      streams: createNewStreamsObjectPatriot(streamsObject),
    }).then(
      () => {
        if (lastCamera) {
          if (patriotCamsWithErrors.length > 0) {
            alert(
              `The following cameras had errors in adding the Patriot alert: ${patriotCamsWithErrors.join(
                ", "
              )}`
            );
          }
        }
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          patriotCamsWithErrors.push(cameraName);
          if (lastCamera) {
            if (patriotCamsWithErrors.length > 0) {
              alert(
                `The following cameras had errors in adding the Patriot alert: ${patriotCamsWithErrors.join(
                  ", "
                )}`
              );
            }
          }
        });
      }
    );
  };

  await AxiosUI.post(
    "/nvr_camera/auto_add/",
    handleCreateAutoAddObject(camerasObject)
  ).then(
    (response: AxiosResponse) => {
      nvrCameraFetchFail = false;
      localStorage.setItem("cameraId", response.data.id);
    },
    (reason: AxiosError) => {
      nvrCameraFetchFail = true;
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        localStorage.setItem("autoAddError", "true");
        // setLargerScreen(true);
        setVisibleScreen(
          <CameraAutoAddScreen
            setVisibleScreen={setVisibleScreen}
            setLargerScreen={setLargerScreen}
            largerScreen={largerScreen}
            setCameraCheckFinsihedAlertOpen={setCameraCheckFinsihedAlertOpen}
            setGoodCameras={setGoodCameras}
            setConnectivityCameras={setConnectivityCameras}
            setLowResolutionCameras={setLowResolutionCameras}
            setCameraCheckResultDownloadURL={setCameraCheckResultDownloadURL}
          />
        );
      });
    }
  );
  !nvrCameraFetchFail &&
    (await ActuateUI.get(
      `camera/site/?customer__id=${localStorage.siteId}`
    ).then(
      (response: AxiosResponse) => {
        siteCamerasFetchFail = false;
        localStorage.setItem("siteCameras", JSON.stringify(response.data));
        const cameraResults = response.data.results;
        siteCameraIds = cameraResults
          .map((camera: { id: number }) => Number(camera.id))
          .filter((id: number) => Number.isFinite(id));
        if (cameraResults.length > 0 && sentinelSite) {
          cameraResults.forEach((camera: any, index: number) => {
            AxiosUI.get(`camera/${camera.id}/general_info/`).then(
              (responseTwo: AxiosResponse) => {
                const streams = responseTwo.data.streams;
                postSentinelAlert(
                  camera.id,
                  streams,
                  camera.camera_name,
                  cameraResults.length === index + 1
                );
              },
              (reason: AxiosError) => {
                universalAPIErrorHandler(
                  reason,
                  navigate,
                  retryFunction,
                  () => {}
                );
              }
            );
          });
        }
        if (cameraResults.length > 0 && patriotSite) {
          cameraResults.forEach((camera: any, index: number) => {
            AxiosUI.get(`camera/${camera.id}/general_info/`).then(
              (responseTwo: AxiosResponse) => {
                const streams = responseTwo.data.streams;
                postPatriotAlert(
                  camera.id,
                  streams,
                  camera.camera_name,
                  cameraResults.length === index + 1
                );
              },
              (reason: AxiosError) => {
                universalAPIErrorHandler(
                  reason,
                  navigate,
                  retryFunction,
                  () => {}
                );
              }
            );
          });
        }
        if (cameraResults.length > 0 && streamChangesMade) {
          cameraResults.forEach((camera: any, index: number) => {
            AxiosUI.patch(
              `camera/${camera.id}/extra_fields/`,
              streamPatchObject
            ).then(
              () => {
                if (cameraResults.length === index + 1) {
                  if (streamPatchErrorsCameras.length > 0) {
                    alert(
                      `The following cameras had errors in adding the stream parameters: ${streamPatchErrorsCameras.join(
                        ", "
                      )}`
                    );
                  }
                }
              },
              (reason: AxiosError) => {
                universalAPIErrorHandler(
                  reason,
                  navigate,
                  retryFunction,
                  () => {
                    streamPatchErrorsCameras.push(camera.camera_name);
                    if (cameraResults.length === index + 1) {
                      if (streamPatchErrorsCameras.length > 0) {
                        alert(
                          `The following cameras had errors in adding the stream parameters: ${streamPatchErrorsCameras.join(
                            ", "
                          )}`
                        );
                      }
                    }
                  }
                );
              }
            );
          });
        }
      },
      (reason: AxiosError) => {
        siteCamerasFetchFail = true;
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
      }
    ));
  if (!nvrCameraFetchFail && !siteCamerasFetchFail) {
    // Auto-add no longer auto-syncs. Mark the site as having unsynced changes;
    // the operator syncs explicitly (the About-page Sync control shows the
    // unsynced state on arrival).
    markSiteUnsynced();
    const successScreen = (
      <StatusNotificationScreen
        styling="absolute first-line:font-black font-mulish text-[20px] right-[190px] text-center text-[#283E6D] top-80"
        text="Camera Add successful!"
      />
    );
    const continueFlow = () => {
      if (
        localStorage.addAdditionalCamera === "true" ||
        localStorage.clickedFromSiteAboutPage === "true"
      ) {
        if (isCameraCheckSupported()) {
          localStorage.setItem("cameraCheckAlertOpen", "true");
          localStorage.setItem("clickedId", localStorage.siteId);
          localStorage.setItem("navigatedFromSitesAboutPage", "true");
          localStorage.setItem("level0Click", "true");
          localStorage.setItem("cameraChecksiteId", localStorage.siteId);
          handleCheckCameras(
            setCameraCheckFinsihedAlertOpen,
            setGoodCameras,
            setConnectivityCameras,
            setLowResolutionCameras,
            localStorage.siteId,
            setCameraCheckResultDownloadURL,
            navigate
          );
        }
        navigate(`/sites/aboutsite/${localStorage.siteId}`);
        localStorage.removeItem("addAdditionalCamera");
      } else if (localStorage.healthcheckOnly === "true") {
        // setVisibleScreen(<ProgressFinishedScreen />);
        navigate(`/sites/aboutsite/${localStorage.siteId}`);
        localStorage.setItem("healthcheckOnly", "false");
      } else {
        setVisibleScreen(
          <OnboardingProgressDiv
            percentage={
              isCameraCheckSupported()
                ? localStorage.healthMonitoringOnly === "true"
                  ? 66
                  : 50
                : 66
            }
            step={2}
            setVisibleScreen={setVisibleScreen}
          />
        );
      }
    };
    // ENG-2219: before moving on, offer to create the Security Center alarms
    // for a Genetec site. The prompt asks admin (GET genetec_alarm_setup)
    // whether there is anything to set up — it renders nothing and calls
    // onDone at once for non-Genetec sites, so the success screen still shows
    // for its usual 2 s. localStorage.integrationType can be stale here (it is
    // set by the site About page), so the backend's is_genetec decides.
    const shownAt = Date.now();
    const continueAfterSuccessScreen = () => {
      setTimeout(
        continueFlow,
        Math.max(0, SUCCESS_SCREEN_MS - (Date.now() - shownAt))
      );
    };
    // ENG-2461: after the Genetec prompt, offer to import ignore zones,
    // privacy masks, names and enabled state the VMS already has for the new
    // cameras. Renders nothing (and continues at once) unless the site's
    // About payload offers `importable_settings` and the preview has values.
    const showImportSettingsPrompt = () => {
      setVisibleScreen(
        <>
          {successScreen}
          <ImportSettingsOnboardingPrompt
            siteId={localStorage.siteId}
            cameraIds={siteCameraIds}
            navigate={navigate}
            onDone={continueAfterSuccessScreen}
          />
        </>
      );
    };
    setVisibleScreen(
      <>
        {successScreen}
        <GenetecAlarmOnboardingPrompt
          siteId={localStorage.siteId}
          navigate={navigate}
          onDone={showImportSettingsPrompt}
        />
      </>
    );
  }
};
